import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Phone, Star, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/Calendar.css';

const RestaurantProfile = () => {
    // State for tabs
    const [activeTab, setActiveTab] = useState('about');
    
    // State for image gallery
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    // State for booking process
    const [bookingStep, setBookingStep] = useState(1); // 1: Date, 2: Time, 3: Guests, 4: Complete
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedTime, setSelectedTime] = useState('');
    const [selectedGuests, setSelectedGuests] = useState(2);
    
    // Custom calendar for date selection
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [calendarDays, setCalendarDays] = useState([]);

    const restaurant = {
        name: "Shalimar",
        location: "92 Pass. Brady, 75010, Paris",
        cuisine: "Indian",
        averagePrice: "€20",
        rating: 9.4,
        reviews: 3342,
        hours: "Mon-Thu: 11am-10pm, Fri-Sat: 11am-11pm, Sun: 12pm-9pm",
        phone: "(123) 456-7890",
        // Multiple restaurant images
        images: [
            "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        "https://images.unsplash.com/photo-1544148103-0773bf10d330?q=80&w=2070&auto=format&fit=crop%22,",
        "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?q=80&w=2067&auto=format&fit=crop",
        
        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=2070&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?q=80&w=2080&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?q=80&w=2067&auto=format&fit=crop"
      
          ],
    };

    // Function to handle image navigation
    const nextImage = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === restaurant.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    // const prevImage = () => {
    //     setCurrentImageIndex((prevIndex) => 
    //         prevIndex === 0 ? restaurant.images.length - 1 : prevIndex - 1
    //     );
    // };
    
    // Calendar functions
    useEffect(() => {
        generateCalendar(currentMonth);
    }, [currentMonth]);
    
    const generateCalendar = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        
        // Get the first day of the month
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        // Get the starting day of the week (0 = Sunday, 1 = Monday, etc.)
        const startingDay = firstDay.getDay();
        
        // Generate days from previous month to fill the first week
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        const prevMonthDays = [];
        for (let i = startingDay - 1; i >= 0; i--) {
            prevMonthDays.push({
                day: prevMonthLastDay - i,
                month: month - 1,
                year,
                isCurrentMonth: false,
                isDisabled: true,
            });
        }
        
        // Generate days for current month
        const currentMonthDays = [];
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const date = new Date(year, month, i);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            currentMonthDays.push({
                day: i,
                month,
                year,
                isCurrentMonth: true,
                isDisabled: date < today,
                hasDiscount: i >= 24, // Show discount on days after 24th
                discountAmount: '30%'
            });
        }
        
        // Generate days for next month to complete the calendar
        const nextMonthDays = [];
        const totalDays = prevMonthDays.length + currentMonthDays.length;
        const remainingDays = 42 - totalDays; // 6 rows × 7 days = 42
        
        for (let i = 1; i <= remainingDays; i++) {
            nextMonthDays.push({
                day: i,
                month: month + 1,
                year,
                isCurrentMonth: false,
                isDisabled: true,
            });
        }
        
        setCalendarDays([...prevMonthDays, ...currentMonthDays, ...nextMonthDays]);
    };
    
    const goToPreviousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };
    
    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };
    
    const isDateSelected = (day, month, year) => {
        return (
            selectedDate.getDate() === day &&
            selectedDate.getMonth() === month &&
            selectedDate.getFullYear() === year
        );
    };

    // Reusable components
    const ReviewCard = ({ title, text, author, date }) => (
        <div className="card mb-3">
            <div className="card-body">
                <h5 className="card-title">{title}</h5>
                {text && <p className="card-text">{text}</p>}
                <p className="card-text">
                    <small className="text-muted">{author} • {date}</small>
                </p>
            </div>
        </div>
    );

    const MenuItem = ({ name, description, price }) => (
        <div className="card mb-3">
            <div className="card-body">
                <h5 className="card-title">{name}</h5>
                <p className="card-text">{description}</p>
                <p className="card-text">
                    <small className="text-muted">{price}</small>
                </p>
            </div>
        </div>
    );

    // Available time slots
    const timeSlots = [
        '12:00', '12:30', '13:00', '13:30', '14:00', 
        '19:00', '19:30', '20:00', '20:30', '21:00'
    ];

    // Handle form submission
    // const handleBookingSubmit = (e) => {
    //     if (e) e.preventDefault();
    //     setBookingStep(4); // Show confirmation screen
    // };

    // Render booking step content
    const renderBookingStep = () => {
        switch(bookingStep) {
            case 1:
                return (
                    <>
                        <h2 className="fs-4 fw-bold mb-3">Book a table for</h2>
                        <p className="mb-2">Book for free</p>
                        
                        <div className="alert alert-info d-flex align-items-center py-2 px-3 mb-4">
                            <div className="text-warning me-2">🔥</div>
                            <div className="flex-grow-1">Already <strong>10 bookings</strong> today</div>
                        </div>
                        
                        {/* Step indicator */}
                        <div className="position-relative mb-4">
                            <div className="progress" style={{ height: "4px" }}>
                                <div className="progress-bar bg-success" style={{ width: "25%" }}></div>
                            </div>
                            <div className="d-flex justify-content-between position-relative" style={{ marginTop: "-12px" }}>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-calendar"></i>
                                </button>
                                <button className="btn btn-outline-secondary rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-clock"></i>
                                </button>
                                <button className="btn btn-outline-secondary rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-person"></i>
                                </button>
                                <button className="btn btn-outline-secondary rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-check-lg"></i>
                                </button>
                            </div>
                        </div>
                        
                        <h3 className="fs-5 text-center my-3">February 2025</h3>
                        
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <button className="btn btn-sm btn-link" onClick={goToPreviousMonth}>
                                <ChevronLeft size={20} />
                            </button>
                            <span className="fs-5">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                            <button className="btn btn-sm btn-link" onClick={goToNextMonth}>
                                <ChevronRight size={20} />
                            </button>
                        </div>
                        
                        <div className="mb-3">
                            <div className="row g-0 text-center mb-2">
                                {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day, index) => (
                                    <div key={index} className="col">
                                        <small className="text-muted">{day}</small>
                                    </div>
                                ))}
                            </div>
                            <div className="row g-1">
                                {calendarDays.map((day, index) => (
                                    <div key={index} className="col p-1" style={{ minHeight: "40px" }}>
                                        <button 
                                            className={`btn w-100 h-100 p-1 rounded-3 position-relative ${
                                                isDateSelected(day.day, day.month, day.year) 
                                                    ? 'btn-primary text-white' 
                                                    : day.isDisabled 
                                                        ? 'text-muted opacity-50' 
                                                        : day.isCurrentMonth 
                                                            ? 'btn-outline-light' 
                                                            : 'text-muted opacity-25'
                                            }`}
                                            disabled={day.isDisabled}
                                            onClick={() => {
                                                if (!day.isDisabled) {
                                                    setSelectedDate(new Date(day.year, day.month, day.day));
                                                }
                                            }}
                                            style={{ 
                                                backgroundColor: isDateSelected(day.day, day.month, day.year) ? '' : day.isCurrentMonth ? 'white' : '#f8f9fa'
                                            }}
                                        >
                                            {day.day}
                                            {day.hasDiscount && (
                                                <span className="position-absolute bottom-0 start-50 translate-middle-x mb-0 badge rounded-pill bg-dark" style={{ fontSize: '0.6rem' }}>
                                                    -{day.discountAmount}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <button 
                            className="btn btn-success w-100 py-3 rounded-4 mt-3"
                            onClick={() => setBookingStep(2)}
                        >
                            Continue to Select Time
                        </button>
                    </>
                );
            case 2:
                return (
                    <>
                        <h2 className="fs-4 fw-bold mb-3">Book a table for</h2>
                        <p className="mb-2">Book for free</p>
                        
                        <div className="alert alert-info d-flex align-items-center py-2 px-3 mb-4">
                            <div className="text-warning me-2">🔥</div>
                            <div className="flex-grow-1">Already <strong>10 bookings</strong> today</div>
                        </div>
                        
                        {/* Step indicator */}
                        <div className="position-relative mb-4">
                            <div className="progress" style={{ height: "4px" }}>
                                <div className="progress-bar bg-success" style={{ width: "50%" }}></div>
                            </div>
                            <div className="d-flex justify-content-between position-relative" style={{ marginTop: "-12px" }}>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    onClick={() => setBookingStep(1)}
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-calendar"></i>
                                </button>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-clock"></i>
                                </button>
                                <button className="btn btn-outline-secondary rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-person"></i>
                                </button>
                                <button className="btn btn-outline-secondary rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-check-lg"></i>
                                </button>
                            </div>
                        </div>
                        
                        <h5 className="mb-3">Select a Time</h5>
                        <p className="mb-3 fw-bold">{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                        
                        <div className="mb-4">
                            <div className="row g-2">
                                {timeSlots.map(time => (
                                    <div className="col-4" key={time}>
                                        <button 
                                            className={`btn ${selectedTime === time ? 'btn-primary' : 'btn-outline-primary'} w-100 py-2`}
                                            onClick={() => setSelectedTime(time)}
                                        >
                                            {time}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="d-flex justify-content-between">
                            <button 
                                className="btn btn-outline-secondary px-4"
                                onClick={() => setBookingStep(1)}
                            >
                                Back
                            </button>
                            <button 
                                className="btn btn-success px-4"
                                onClick={() => setBookingStep(3)}
                                disabled={!selectedTime}
                            >
                                Continue
                            </button>
                        </div>
                    </>
                );
            case 3:
                return (
                    <>
                        <h2 className="fs-4 fw-bold mb-3">Book a table for</h2>
                        <p className="mb-2">Book for free</p>
                        
                        <div className="alert alert-info d-flex align-items-center py-2 px-3 mb-4">
                            <div className="text-warning me-2">🔥</div>
                            <div className="flex-grow-1">Already <strong>10 bookings</strong> today</div>
                        </div>
                        
                        {/* Step indicator */}
                        <div className="position-relative mb-4">
                            <div className="progress" style={{ height: "4px" }}>
                                <div className="progress-bar bg-success" style={{ width: "75%" }}></div>
                            </div>
                            <div className="d-flex justify-content-between position-relative" style={{ marginTop: "-12px" }}>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    onClick={() => setBookingStep(1)}
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-calendar"></i>
                                </button>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    onClick={() => setBookingStep(2)}
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-clock"></i>
                                </button>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-person"></i>
                                </button>
                                <button className="btn btn-outline-secondary rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-check-lg"></i>
                                </button>
                            </div>
                        </div>
                        
                        <h5 className="mb-3">Select Number of Guests</h5>
                        <p className="mb-1 fw-bold">{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                        <p className="mb-3 fw-bold">Time: {selectedTime}</p>
                        
                        <div className="mb-4">
                            <div className="row g-2">
                                {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                    <div className="col-3" key={num}>
                                        <button 
                                            className={`btn ${selectedGuests === num ? 'btn-primary' : 'btn-outline-primary'} w-100 py-2`}
                                            onClick={() => setSelectedGuests(num)}
                                        >
                                            {num}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="d-flex justify-content-between">
                            <button 
                                className="btn btn-outline-secondary px-4"
                                onClick={() => setBookingStep(2)}
                            >
                                Back
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-success px-4"
                                onClick={() => setBookingStep(4)}
                            >
                                Continue
                            </button>
                        </div>
                    </>
                );
            case 4:
                return (
                    <>
                        <h2 className="fs-4 fw-bold mb-3">Book a table for</h2>
                        <p className="mb-2">Book for free</p>
                        
                        <div className="alert alert-info d-flex align-items-center py-2 px-3 mb-4">
                            <div className="text-warning me-2">🔥</div>
                            <div className="flex-grow-1">Already <strong>10 bookings</strong> today</div>
                        </div>
                        
                        {/* Step indicator */}
                        <div className="position-relative mb-4">
                            <div className="progress" style={{ height: "4px" }}>
                                <div className="progress-bar bg-success" style={{ width: "100%" }}></div>
                            </div>
                            <div className="d-flex justify-content-between position-relative" style={{ marginTop: "-12px" }}>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    onClick={() => setBookingStep(1)}
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-calendar"></i>
                                </button>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    onClick={() => setBookingStep(2)}
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-clock"></i>
                                </button>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    onClick={() => setBookingStep(3)}
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-person"></i>
                                </button>
                                <button className="btn btn-success rounded-circle d-flex justify-content-center align-items-center p-0" 
                                    style={{ width: "36px", height: "36px" }}>
                                    <i className="bi bi-check-lg"></i>
                                </button>
                            </div>
                        </div>
                        
                        <div className="text-center mb-4">
                            <div className="display-1 text-success mb-3">✓</div>
                            <h3 className="fs-4 mb-2">Booking Confirmed!</h3>
                            <p className="mb-0">Your reservation has been successfully booked.</p>
                        </div>
                        
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">Reservation Details</h5>
                                <p><strong>Date:</strong> {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                                <p><strong>Time:</strong> {selectedTime}</p>
                                <p><strong>Guests:</strong> {selectedGuests} {selectedGuests === 1 ? 'person' : 'people'}</p>
                                <p><strong>Restaurant:</strong> {restaurant.name}</p>
                                <p className="text-success mb-0"><strong>Status:</strong> Confirmed</p>
                            </div>
                        </div>
                        
                        <button 
                            className="btn btn-outline-primary w-100"
                            onClick={() => setBookingStep(1)}
                        >
                            Make Another Reservation
                        </button>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="container-fluid p-0">
            {/* Breadcrumb */}
            <div className="container mt-3">
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/">Paris</Link>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            {restaurant.name} - Paris
                        </li>
                    </ol>
                </nav>
            </div>

            {/* Restaurant Header */}
            <div className="container mt-4">
                <div className="row">
                    <div className="col-md-8">
                        <div className="position-relative">
                        <img 
  src={restaurant.images[currentImageIndex]} 
  alt={`${restaurant.name} view ${currentImageIndex + 1}`} 
  className="img-fluid rounded" 
  style={{ height: '400px', width: '100%', objectFit: 'cover' }} 
/>

                            {/* <button 
                                className="btn btn-light rounded-circle position-absolute top-50 start-0 translate-middle-y ms-2"
                                onClick={prevImage}
                                style={{ opacity: 0.8 }}
                            >
                                <ChevronLeft size={20} />
                            </button> */}
                            <button 
                                className="btn btn-light rounded-circle position-absolute top-50 end-0 translate-middle-y me-2"
                                onClick={nextImage}
                                style={{ opacity: 0.8 }}
                            >
                                <ChevronRight size={20} />
                            </button>
                            <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3">
                                <div className="d-flex gap-1">
                                    {restaurant.images.map((_, index) => (
                                        <button 
                                            key={index}
                                            className={`btn btn-sm rounded-circle p-1 ${currentImageIndex === index ? 'btn-primary' : 'btn-light'}`}
                                            onClick={() => setCurrentImageIndex(index)}
                                            style={{ width: '12px', height: '12px', padding: 0 }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* overflow-auto */}
                        <div className="d-flex gap-2 mt-2">
                            {restaurant.images.map((img, index) => (
                                <img 
                                    key={index}
                                    src={img} 
                                    alt={`Thumbnail ${index}`}
                                    className={`rounded cursor-pointer ${currentImageIndex === index ? 'border border-primary' : ''}`}
                                    style={{ height: '60px', width: '80px', objectFit: 'cover', cursor: 'pointer' }}
                                    onClick={() => setCurrentImageIndex(index)}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <h1 className="h2 mb-3">{restaurant.name}</h1>
                        <p>{restaurant.location}</p>
                        <p>{restaurant.cuisine} • Average price {restaurant.averagePrice}</p>
                        <div className="d-flex align-items-center mb-3">
                            <Star className="text-warning me-2" />
                            <span className="h5 mb-0 me-2">{restaurant.rating}</span>
                            <span>({restaurant.reviews} reviews)</span>
                        </div>
                        <button 
                            className="btn btn-primary w-100 mb-2"
                            onClick={() => {
                                setBookingStep(1);
                                document.getElementById('booking-widget').scrollIntoView({ behavior: 'smooth' });
                            }}
                        >
                            Book a table
                        </button>
                        <p className="text-muted small">Already 10 bookings today</p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mt-4">
                <div className="row">
                    <div className="col-md-8">
                        {/* Tabs */}
                        <ul className="nav nav-tabs mb-4">
                            <li className="nav-item">
                                <button 
                                    className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('about')}
                                >
                                    About
                                </button>
                            </li>
                            <li className="nav-item">
                                <button 
                                    className={`nav-link ${activeTab === 'menu' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('menu')}
                                >
                                    Menu
                                </button>
                            </li>
                            <li className="nav-item">
                                <button 
                                    className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('reviews')}
                                >
                                    Reviews
                                </button>
                            </li>
                        </ul>

                        {/* About section */}
                        {activeTab === 'about' && (
                            <section id="about" className="mb-5">
                                <h2 className="h4 mb-3">About {restaurant.name}</h2>
                                <p>
                                    Authentic Indian cuisine in a warm and inviting atmosphere. We offer a wide selection of dishes,
                                    including tandoori specialties, curries, and vegetarian options. Come and experience the flavors of
                                    India!
                                </p>
                                <p>
                                    At {restaurant.name}, we believe in providing not just food, but an experience that transports you to the vibrant 
                                    streets of India. Our chefs, trained in authentic Indian cooking techniques, use only the finest ingredients 
                                    and traditional spice blends to create dishes that are both delicious and memorable.
                                </p>
                                <p>
                                    Our restaurant has been serving the Paris community since 1998, and we have built a reputation for excellence in both 
                                    food quality and service. Whether you're looking for a casual lunch, a romantic dinner, or a place to celebrate a special 
                                    occasion, {restaurant.name} offers the perfect setting.
                                </p>
                                <p>
                                    We pride ourselves on our ability to accommodate various dietary preferences, with a wide range of vegetarian and vegan options 
                                    available. Our menu also includes gluten-free choices, ensuring that everyone can enjoy our authentic Indian flavors.
                                </p>
                            </section>
                        )}

                        {/* Menu section */}
                        {activeTab === 'menu' && (
                            <section id="menu" className="mb-5">
                                <h2 className="h4 mb-3">Menu</h2>
                                
                                <h5 className="mt-4 mb-3">Breads</h5>
                                <MenuItem 
                                    name="Chappati"
                                    description="Pain nature, farine complète non levée"
                                    price="€2"
                                />
                                <MenuItem 
                                    name="Nan"
                                    description="Pain nature, farine blanche pate levée"
                                    price="€2.5"
                                />
                                <MenuItem 
                                    name="Paratha"
                                    description="Pain beurre, farine complète non levée"
                                    price="€4"
                                />
                                
                                <h5 className="mt-4 mb-3">Appetizers</h5>
                                <MenuItem 
                                    name="Samosa"
                                    description="Crispy pastry filled with spiced potatoes and peas"
                                    price="€6"
                                />
                                <MenuItem 
                                    name="Pakora"
                                    description="Vegetables dipped in chickpea flour batter and deep-fried"
                                    price="€5.5"
                                />
                                <MenuItem 
                                    name="Chicken Tikka"
                                    description="Marinated chicken pieces grilled in tandoor"
                                    price="€8"
                                />
                                
                                <h5 className="mt-4 mb-3">Main Courses</h5>
                                <MenuItem 
                                    name="Butter Chicken"
                                    description="Tandoori chicken cooked in a rich tomato and butter sauce"
                                    price="€16"
                                />
                                <MenuItem 
                                    name="Lamb Rogan Josh"
                                    description="Aromatic lamb curry with a blend of traditional spices"
                                    price="€18"
                                />
                                <MenuItem 
                                    name="Palak Paneer"
                                    description="Cottage cheese cubes in a creamy spinach sauce"
                                    price="€14"
                                />
                                
                                <button className="btn btn-outline-primary mt-3">Explore full menu</button>
                            </section>
                        )}

                        {/* Reviews section */}
                        {activeTab === 'reviews' && (
                            <section id="reviews">
                                <h2 className="h4 mb-3">Reviews</h2>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <span className="h2 text-primary">{restaurant.rating}</span>
                                        <span className="text-muted"> / 10</span>
                                    </div>
                                    <div>
                                        <span className="text-muted">{restaurant.reviews} reviews</span>
                                    </div>
                                </div>
                                <ReviewCard 
                                    title="Great. nothing more to add. I will definitely repeat."
                                    author="manuel d."
                                    date="February 21, 2025"
                                />
                                <ReviewCard 
                                    title="10/10"
                                    text="I recently dined at old shalimar and was thoroughly impressed by the experience. The ambiance was warm and inviting, making it a perfect spot for a cozy meal. The service was impeccable..."
                                    author="Dr D."
                                    date="January 27, 2025"
                                />
                                <ReviewCard 
                                    title="A taste of authentic Indian cuisine"
                                    text="This place never disappoints! The butter chicken is to die for, and the naan bread is always perfectly cooked. The staff are friendly and attentive."
                                    author="Sarah M."
                                    date="January 15, 2025"
                                />
                                <ReviewCard 
                                    title="Perfect for special occasions"
                                    text="We celebrated our anniversary here and had a wonderful time. The food was delicious, and they really made our evening special with a complimentary dessert."
                                    author="John and Lisa"
                                    date="December 30, 2024"
                                />
                                <ReviewCard 
                                    title="Best Indian in Paris"
                                    text="I've tried many Indian restaurants in Paris, and this one tops them all. Authentic flavors, generous portions, and reasonable prices."
                                    author="Pierre L."
                                    date="December 12, 2024"
                                />
                                <button className="btn btn-outline-primary">See more reviews (1494)</button>
                            </section>
                        )}
                    </div>

                    <div className="col-md-4">
                        {/* Booking widget */}
                        <div id="booking-widget" className="card mb-4 border-0 shadow-sm">
                            <div className="card-body p-4">
                                {renderBookingStep()}
                            </div>
                        </div>

                        {/* Restaurant details */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">Restaurant details</h5>
                                <div className="mb-2">
                                    <MapPin className="me-2" />
                                    <span>{restaurant.location}</span>
                                </div>
                                <div className="mb-2">
                                    <Clock className="me-2" />
                                    <span>{restaurant.hours}</span>
                                </div>
                                <div className="mb-2">
                                    <Phone className="me-2" />
                                    <span>{restaurant.phone}</span>
                                </div>
                            </div>
                        </div>

                        {/* Features */}
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Features</h5>
                                <ul className="list-unstyled">
                                    {['Vegetarian dishes', 'Outdoor dining', 'Romantic', 'Family-friendly', 'Takeaway available', 'Delivery available', 'Wheelchair accessible'].map((feature) => (
                                        <li key={feature} className="mb-2">
                                            <Heart className="me-2" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RestaurantProfile;